/**
 * LinkVbaGroupModal — client-admin action to adopt a pre-existing VBA group
 * into an Account.
 *
 * CBS sometimes creates a group in VBA before the matching Account is built in
 * Infinity, which leaves the same employer showing up twice on the Accounts
 * list (the live VBA "Active" virtual card and the Infinity Account). Linking
 * stamps `Group.vbaGroupId`, which is the join key every downstream surface
 * dedupes/merges on — so the duplicate card drops and the live VBA group
 * becomes the surviving system of record with the Account's data layered on.
 *
 * The picker seeds its search with the account's company name to surface likely
 * matches (also matches Group_ID / Federal_ID); already-linked VBA groups are
 * filtered out server-side so they can't be double-linked.
 */

import React from 'react';
import {Building2, Check, Loader2, Search} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@common-web/components/ui/dialog';
import {Button} from '@common-web/components/ui/button';
import {Input} from '@common-web/components/ui/input';
import {trpc, type RouterOutput} from '@common-web/lib/trpc';
import {toast} from '@common-web/hooks/use-toast';
import {useDebounce} from '@common-web/hooks/use-debounce';
import {cn} from '@common-web/lib/utils';

type LinkableVbaGroup =
  RouterOutput['account']['searchLinkableVbaGroups']['groups'][number];

interface LinkVbaGroupModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  accountsId: string;
  /** Seeds the search so likely matches show first; usually the company name. */
  defaultSearch: string;
  /** Called after a successful link so the parent can refresh account data. */
  onLinked: () => void;
}

function formatLocation(city: string | null, state: string | null): string {
  return [city, state].filter(Boolean).join(', ');
}

export function LinkVbaGroupModal({
  open,
  onOpenChange,
  accountsId,
  defaultSearch,
  onLinked,
}: LinkVbaGroupModalProps): React.ReactElement {
  const [search, setSearch] = React.useState(defaultSearch);
  const [selected, setSelected] = React.useState<string | null>(null);
  const debouncedSearch = useDebounce(search, 300);

  // Re-seed the search and clear the selection whenever the modal (re)opens.
  React.useEffect(() => {
    if (open) {
      setSearch(defaultSearch);
      setSelected(null);
    }
  }, [open, defaultSearch]);

  const query = trpc.account.searchLinkableVbaGroups.useQuery(
    {accountsId, search: debouncedSearch || undefined},
    {enabled: open, staleTime: 30_000},
  );

  const linkMutation = trpc.account.linkVbaGroup.useMutation({
    onSuccess: () => {
      toast({title: 'Linked', description: 'The account is now linked to the live VBA group.'});
      onLinked();
      onOpenChange(false);
    },
    onError: (err) => {
      toast({title: 'Could not link', description: err.message, variant: 'destructive'});
    },
  });

  const groups = query.data?.groups ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-lg'>
        <DialogHeader>
          <DialogTitle>Link existing VBA group</DialogTitle>
          <DialogDescription>
            Adopt a group CBS already set up in VBA. Once linked, the live VBA group becomes the
            single source of truth and this account&apos;s data is shown alongside it.
          </DialogDescription>
        </DialogHeader>

        <div className='min-w-0 space-y-3'>
          <div className='relative'>
            <Search className='pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground' />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder='Search by name, VBA group ID, or Federal ID'
              className='pl-9'
            />
          </div>

          <div className='max-h-72 min-w-0 overflow-y-auto rounded-md border'>
            {query.isLoading ? (
              <div className='flex items-center justify-center gap-2 p-6 text-sm text-muted-foreground'>
                <Loader2 className='h-4 w-4 animate-spin' />
                Searching VBA…
              </div>
            ) : query.error ? (
              <div className='p-6 text-sm text-red-600'>{query.error.message}</div>
            ) : groups.length === 0 ? (
              <div className='p-6 text-center text-sm text-muted-foreground'>
                No matching unlinked VBA groups found.
              </div>
            ) : (
              <ul className='divide-y'>
                {groups.map((g: LinkableVbaGroup) => {
                  const isSelected = selected === g.vbaGroupId;
                  return (
                    <li key={g.vbaGroupId}>
                      <button
                        type='button'
                        onClick={() => setSelected(g.vbaGroupId)}
                        className={cn(
                          'flex w-full items-start gap-3 px-3 py-2.5 text-left transition-colors hover:bg-muted/60',
                          isSelected && 'bg-primary/5',
                        )}
                      >
                        <Building2 className='mt-0.5 h-4 w-4 shrink-0 text-muted-foreground' />
                        <div className='min-w-0 flex-1'>
                          <div className='truncate text-sm font-medium'>
                            {g.name ?? g.vbaGroupId}
                          </div>
                          <div className='mt-0.5 flex flex-wrap gap-x-3 text-xs text-muted-foreground'>
                            <span className='font-mono'>{g.vbaGroupId}</span>
                            {g.federalId ? <span>EIN {g.federalId}</span> : null}
                            {formatLocation(g.city, g.state) ? (
                              <span>{formatLocation(g.city, g.state)}</span>
                            ) : null}
                          </div>
                        </div>
                        {isSelected ? (
                          <Check className='mt-0.5 h-4 w-4 shrink-0 text-primary' />
                        ) : null}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button type='button' variant='outline' onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type='button'
            disabled={!selected || linkMutation.isPending}
            onClick={() => {
              if (selected) {
                linkMutation.mutate({accountsId, vbaGroupId: selected});
              }
            }}
          >
            {linkMutation.isPending ? (
              <>
                <Loader2 className='mr-1.5 h-4 w-4 animate-spin' />
                Linking…
              </>
            ) : (
              'Link group'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
